// src/utils/simulationUtils.js
// Calculs de rendement locatif. Fonctions pures, sans React.

export const SIM_DEFAULTS = {
  notaryFeesRate: 0.08,        // 8% du prix
  vacancyRate: 0.05,           // 5% de vacance locative annuelle
  annualMaintenanceRate: 0.01, // 1% du prix d'achat / an pour entretien
  loanRate: 3.5,               // taux annuel en %
  loanDurationMonths: 240,     // 20 ans
};

// Mensualité crédit — formule classique d'annuité.
// loanAmount : montant emprunté (€)
// ratePercent : taux annuel en pourcentage (ex: 3.5)
// durationMonths : durée en mois (ex: 240)
export function computeMonthlyMortgage(loanAmount, ratePercent, durationMonths) {
  if (!loanAmount || loanAmount <= 0 || !durationMonths) return 0;
  const r = (ratePercent / 100) / 12;
  if (r === 0) return loanAmount / durationMonths;
  return (loanAmount * r * Math.pow(1 + r, durationMonths)) / (Math.pow(1 + r, durationMonths) - 1);
}

// Calcul complet de la simulation à partir des hypothèses.
// Renvoie tous les indicateurs : rendements, cashflow, coûts cumulés.
export function computeSimulation(input) {
  const {
    purchasePrice = 0,
    notaryFeesRate = SIM_DEFAULTS.notaryFeesRate,
    downPayment = 0,
    monthlyRent = 0,
    monthlyCharges = 0,
    annualPropertyTax = 0,
    vacancyRate = SIM_DEFAULTS.vacancyRate,
    annualMaintenanceRate = SIM_DEFAULTS.annualMaintenanceRate,
    loanRate = SIM_DEFAULTS.loanRate,
    loanDurationMonths = SIM_DEFAULTS.loanDurationMonths,
  } = input || {};

  const notaryFees = purchasePrice * notaryFeesRate;
  const totalCost = purchasePrice + notaryFees;
  const loanAmount = Math.max(0, totalCost - downPayment);
  const monthlyMortgage = computeMonthlyMortgage(loanAmount, loanRate, loanDurationMonths);

  const annualRent = monthlyRent * 12;
  const annualEffectiveRent = annualRent * (1 - vacancyRate);
  const annualCharges = monthlyCharges * 12;
  const annualMaintenance = purchasePrice * annualMaintenanceRate;
  const annualNetIncome = annualEffectiveRent - annualCharges - annualPropertyTax - annualMaintenance;

  const grossYield = totalCost > 0 ? annualRent / totalCost : 0;
  const netYield = totalCost > 0 ? annualNetIncome / totalCost : 0;

  const monthlyCashflow =
    monthlyRent - monthlyCharges - (annualPropertyTax / 12) - monthlyMortgage - (annualMaintenance / 12);

  const totalCreditCost = monthlyMortgage * loanDurationMonths;
  const interestPaid = totalCreditCost - loanAmount;
  const totalEffort = monthlyCashflow * loanDurationMonths; // négatif = à sortir de sa poche

  return {
    notaryFees,
    totalCost,
    loanAmount,
    monthlyMortgage,
    annualNetIncome,
    grossYield,
    netYield,
    monthlyCashflow,
    totalCreditCost,
    interestPaid,
    totalEffort,
  };
}
